import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { HistoryModuleTabsComponent } from './history-module-tabs/history-module-tabs.component';
import { CommonModule } from '@angular/common';
import { HistorySalesShowcaseComponent } from '../history-sales/components';
import { HistorySalesDataSentShowcaseComponent } from '../history-sales-data-sent/components/history-sales-data-sent-showcase/history-sales-data-sent-showcase.component';
import { UpgradeUiViewDirective } from 'src/ajs-to-angular';
import { ConfigurationManagerService, ToolbarEventsService } from 'src/app/core';
import { $state } from 'app/ajs-upgraded-providers';
import { TranslateService } from '@ngx-translate/core';
import { HistoryModuleSections } from '../models/history.module';
import { Transition } from 'angular-ui-router';
import { Sales } from 'tilby-models';
import { HistoryDataCatalogService } from '../services/history-data-catalog.service';
import { ToolbarLayoutService } from 'src/app/shared/services/toolbar-layout.service';
import { HistoryEInvoicesShowcaseComponent } from '../history-e-invoices/components/history-e-invoices-showcase/history-e-invoices-showcase.component';

@Component({
  selector: 'app-history-module',
  standalone: true,
  imports: [ HistoryModuleTabsComponent, CommonModule, UpgradeUiViewDirective,
    HistorySalesShowcaseComponent, HistorySalesDataSentShowcaseComponent,
    HistoryEInvoicesShowcaseComponent
  ],
  templateUrl: './history-module.component.html',
  styleUrl: './history-module.component.scss'
})
export class HistoryModuleComponent implements OnInit, OnDestroy {

  private readonly state = inject($state);
  private readonly translate = inject(TranslateService);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly historyDataCatalogService = inject(HistoryDataCatalogService);
  private readonly toolbarLayoutService = inject(ToolbarLayoutService);
  private readonly configurationManager = inject(ConfigurationManagerService);
  
  private unsubscribeTransition: Function | undefined;
  protected isNavigating = false;
  protected isLoading = false;
  protected isZpmsEnabled = this.configurationManager.getPreference('zucchetti_pms.send_other_sales');

  modulePath = this.state.$current.name.includes('app.new.history.module');

  tabChange: number = -1;
  tabActive: HistoryModuleSections | null = null;
  tabsLength: number = 1;

  salesDataSentList: Sales[] = [];

  protected readonly permissions: Record<string, boolean>;
  protected readonly activeTabsCount: number;
  protected readonly singleTabType: string;

  constructor() {
    this.permissions = {
      sales: true,
      e_invoices: this.configurationManager.getShopCountry() == 'IT' && this.configurationManager.isModuleAngular('history.e_invoices'),
      sales_data_sent: !!this.configurationManager.getPreference('zucchetti_pms.send_other_sales')
    };

    const activeEntries = Object.entries(this.permissions)
                                .filter(([_, enabled]) => enabled);

    this.activeTabsCount = activeEntries.length;
    this.singleTabType = this.activeTabsCount === 1 ? activeEntries[0][0] : '';

    this.unsubscribeTransition = this.state.router.transitionService.onSuccess({}, (transition: Transition&any) => {
      const stateName = transition.to().name;
      this.modulePath = stateName?.includes('app.new.history.module');
      this.syncTabWithState(stateName);
    })
  }

  async ngOnInit() {
    this.historyDataCatalogService.addDataToCatalog('sales-data-sent', this.salesDataSentList);
    this.initPreferencies();

    this.syncTabWithState(this.state?.current?.name);
  }

  initPreferencies() {
    this.configurationManager.setShopPreference("history.sales_data_sent.massive_send_time_limit", 900);
  }

  private syncTabWithState(stateName: string) {
    if (!stateName) return; 
    this.toolbarLayoutService.showModuleTitle$.next(false);

    let newTabChange = -1;
    let newTabActive: HistoryModuleSections | null = null;
    let moduleTitleText = "";

    if (stateName.includes('e_invoices')) {
      newTabActive = 'e_invoices';
      newTabChange = 1;
      moduleTitleText = "E_INVOICES";
    } else if (stateName.includes('sales_data_sent')) {
      newTabActive = 'sales_data_sent';
      newTabChange = 2;
      moduleTitleText = "SALES_DATA_SENT";
    } else if (stateName.includes('sales')) {
      newTabActive = 'sales';
      newTabChange = 0;
      moduleTitleText = "SALES";
    }

    if (this.tabChange !== newTabChange) {
      this.tabActive = newTabActive;
      this.tabChange = newTabChange;
      if(moduleTitleText) {
        this.toolbarEventsService.moduleTitle.next(moduleTitleText);
      }
    }
  }

  changeTab(textLabelPanel: any) {
    if (this.isLoading || this.isNavigating || this.state.router.globals.transition) {
      // console.log("changeTab: navigazione bloccata (isNavigating o isLoading o || this.state.router.globals.transition)");
      return;
    }

    let targetSection: HistoryModuleSections | null = null;
    let targetRoute: string | null = null;

    if (textLabelPanel === this.translate.instant("HISTORY.TOPBAR_HISTORY_MODULE.SALES")) {
      targetSection = 'sales';
      targetRoute = 'app.new.history.module.sales';
    }
    else if (textLabelPanel === this.translate.instant("HISTORY.TOPBAR_HISTORY_MODULE.E_INVOICES")) {
      targetSection = 'e_invoices';
      targetRoute = 'app.new.history.module.e_invoices';
    }
    else if (textLabelPanel === this.translate.instant("HISTORY.TOPBAR_HISTORY_MODULE.SALES_DATA_SENT")) {
      targetSection = 'sales_data_sent';
      targetRoute = 'app.new.history.module.sales_data_sent';
    }

    if (this.tabActive === targetSection) {
      // console.log("Tab già attivo, blocco navigazione ridondante");
      return;
    }

    this.isNavigating = true;
    this.tabActive = targetSection; 

    this.state.go(targetRoute).finally(() => {
      setTimeout(() => {
        this.isNavigating = false;
      });
    });
  }

  ngOnDestroy(): void {
    if (this.unsubscribeTransition) {
      this.unsubscribeTransition();
    }
    this.toolbarLayoutService.showModuleTitle$.next(true);
  }
}